import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { LeaguesScore } from './LeaguesScore';
import {
  LeagueLeaderboardEntry,
  leaderboardSize,
} from 'src/replicant/match2/leagues/leagues.ruleset';
import { LeaguesPrize } from './LeaguesPrize';
import { getLeagueReward } from 'src/replicant/match2/leagues/leagues.getters';

export class LeaguesLeadeboardView extends View {
  private readonly bg: ImageScaleView;
  private readonly positionText: LangBitmapFontTextView;
  private readonly positionIcon: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly score: LeaguesScore;
  private readonly prize: LeaguesPrize;
  private readonly left: View;
  private readonly right: View;
  private readonly message: LangBitmapFontTextView;

  constructor(opts: ViewOpts) {
    super({ width: 520, height: 70, ...opts });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 7,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.left = new View({
      superview: this,
    });

    this.right = new View({
      superview: this,
    });

    this.message = new LangBitmapFontTextView({
      superview: this,
      x: 0,
      y: 0,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: this.style.width,
      height: this.style.height,
      color: '#754111',
      visible: false,
    });

    this.name = new LangBitmapFontTextView({
      superview: this.left,
      x: 140,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 150,
      height: this.style.height,
      color: '#754111',
    });

    this.positionText = new LangBitmapFontTextView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: this.style.height,
      height: this.style.height,
    });

    this.positionIcon = new ImageScaleView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.avatar = new Avatar({
      superview: this.left,
      width: 58,
      height: 58,
      x: 105,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      frame: 'league_crown',
    });

    this.score = new LeaguesScore({
      superview: this.right,
      x: this.style.width - 130,
      y: 0,
      width: 80,
      height: this.style.height,
    });

    this.prize = new LeaguesPrize({
      superview: this.right,
      x: this.style.width - 190,
      y: this.style.height * 0.5,
      width: 65,
      height: 65,
    });
  }

  public setup(
    params:
      | { message: string }
      | (LeagueLeaderboardEntry & { hideReward?: boolean }),
  ) {
    if ('message' in params) {
      this.message.localeText = () => params.message;
      this.message.show();
      this.name.hide();
      this.score.hide();
      this.avatar.hide();
      this.prize.hide();
      return;
    }

    this.name.show();
    this.score.show();
    this.avatar.show();
    this.prize.show();
    this.message.hide();

    const alt = params.me;
    this.name.localeText = () => params.name;
    this.name.updateOpts({
      color: alt ? '#FFFFFF' : '#754111',
      font: alt ? 'Title' : 'Body',
    });
    this.score.setup(params);

    if (params.rank >= 1 && params.rank <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/match2/tournament/star_tournament_medal_${params.rank}.png`,
        visible: true,
      });
      this.positionText.updateOpts({ visible: false });
    } else {
      this.positionText.updateOpts({ visible: true });
      this.positionIcon.updateOpts({ visible: false });
      this.positionText.localeText = () =>
        params.rank <= leaderboardSize ? params.rank.toString() : '—';
    }

    this.avatar.updateImage(params.photo);

    const prize = params.hideReward ? false : getLeagueReward(params.rank);

    if (prize) {
      this.prize.updateOpts({ visible: true });
      this.prize.setup(prize[0]);
    } else {
      this.prize.updateOpts({ visible: false });
    }

    this.left.updateOpts({ offsetX: 0 });
    this.right.updateOpts({ offsetX: 0 });
    this.bg.updateOpts({
      offsetY: 0,
      image: 'assets/ui/match2/common/list_entry.png',
    });

    if (params.me) {
      this.bg.updateOpts({
        image: `assets/ui/match2/leagues/you_frame.png`,
      });
    }
  }

  public async grantReward(root: View) {
    await this.prize.grantReward(root);
  }
}
